import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMediaQuery } from "@/hooks/use-media-query";

interface Transaction {
  id: string;
  amount: number;
  verification_code: string;
  created_at: string;
  is_confirmed: boolean;
  months: number;
  payment_date: string | null;
  payment_gateway: string | null;
}

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    const fetchTransactions = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/');
        return;
      }

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching transactions:', error);
        return;
      }

      setTransactions(data || []);
      setIsLoading(false);
    };

    fetchTransactions();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Đang tải...</p>
      </div>
    );
  }

  const renderMobileView = () => (
    <div className="space-y-4">
      {transactions.map((transaction) => (
        <Card 
          key={transaction.id}
          className="p-4 cursor-pointer hover:bg-accent"
          onClick={() => navigate(`/nangcap?amount=${transaction.amount}&des=${transaction.verification_code}`)}
        >
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium">Ngày tạo:</span>
              <span>{new Date(transaction.created_at).toLocaleDateString('vi-VN')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Mã giao dịch:</span>
              <span>{transaction.verification_code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Số tháng:</span>
              <span>{transaction.months} tháng</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Số tiền:</span>
              <span>{transaction.amount.toLocaleString()}đ</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Trạng thái:</span>
              <span className={transaction.is_confirmed ? "text-green-500" : "text-yellow-500"}>
                {transaction.is_confirmed ? "Đã thanh toán" : "Chờ thanh toán"}
              </span>
            </div>
            {transaction.payment_date && (
              <div className="flex justify-between">
                <span className="text-sm font-medium">Ngày thanh toán:</span>
                <span>{new Date(transaction.payment_date).toLocaleDateString('vi-VN')}</span>
              </div>
            )}
            {transaction.payment_gateway && (
              <div className="flex justify-between">
                <span className="text-sm font-medium">Cổng thanh toán:</span>
                <span>{transaction.payment_gateway}</span>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );

  const renderDesktopView = () => (
    <Card className="overflow-hidden">
      <ScrollArea className="w-full">
        <div className="min-w-[800px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ngày tạo</TableHead>
                <TableHead>Mã giao dịch</TableHead>
                <TableHead>Số tháng</TableHead>
                <TableHead>Số tiền</TableHead>
                <TableHead>Trạng thái</TableHead>
                <TableHead>Ngày thanh toán</TableHead>
                <TableHead>Cổng thanh toán</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow 
                  key={transaction.id}
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => navigate(`/nangcap?amount=${transaction.amount}&des=${transaction.verification_code}`)}
                >
                  <TableCell>
                    {new Date(transaction.created_at).toLocaleDateString('vi-VN')}
                  </TableCell>
                  <TableCell>{transaction.verification_code}</TableCell>
                  <TableCell>{transaction.months} tháng</TableCell>
                  <TableCell>
                    {transaction.amount.toLocaleString()}đ
                  </TableCell>
                  <TableCell>
                    {transaction.is_confirmed ? (
                      <span className="text-green-500">Đã thanh toán</span>
                    ) : (
                      <span className="text-yellow-500">Chờ thanh toán</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {transaction.payment_date ? 
                      new Date(transaction.payment_date).toLocaleDateString('vi-VN') : 
                      '-'
                    }
                  </TableCell>
                  <TableCell>
                    {transaction.payment_gateway || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </ScrollArea>
    </Card>
  );

  return (
    <div className="container max-w-6xl py-4 sm:py-8 px-2 sm:px-4">
      <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Lịch sử giao dịch</h1>
      {isMobile ? renderMobileView() : renderDesktopView()}
    </div>
  );
};

export default TransactionHistory;